import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { getImageUrl } from "../../../core/api";
import {
  deleteCategory,
  deleteSubCategory,
  getCategories,
} from "../../../core/services/categoryService";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar, { slicePage } from "../../../shared/PaginationBar";

type Category = {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  image?: string;
  isActive?: boolean;
  productCount?: number;
  subCategories?: SubCategory[];
};

type SubCategory = {
  _id: string;
  name: string;
  image?: string;
  slug?: string;
  category?: string | { _id?: string; name?: string };
};

const CategoriesList = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const [categorySearch, setCategorySearch] = useState("");
  const [isActive, setIsActive] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadData = async () => {
    setLoading(true);

    try {
      const categoryData = await getCategories();
      setCategories(categoryData.categories || []);
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const productCount = (item: Category) => Number(item.productCount || 0);

  const filteredCategories = categories.filter((category) => {
    const searchMatch = category.name
      .toLowerCase()
      .includes(categorySearch.toLowerCase());

    const statusMatch =
      isActive === "all" ||
      (category.isActive ?? false) === (isActive === "true");

    return searchMatch && statusMatch;
  });

  useEffect(() => {
    setPage(1);
  }, [categorySearch, isActive]);

  const paged = slicePage(filteredCategories, page, limit);

  const deleteItem = async (id: string, name: string) => {
    const confirmation = await Swal.fire({
      title: `Delete ${name}?`,
      text: "Its subcategories will also be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const result = await deleteCategory(id);

      if (!result.success) {
        throw new Error(result.message || "Delete failed");
      }

      await Swal.fire({
        title: "Deleted",
        text: result.message || "Deleted successfully",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });

      await loadData();
    } catch (error: any) {
      Swal.fire("Delete Failed", error.message, "error");
    }
  };

  const editItem = (id: string) => {
    const item = categories.find((category) => category._id === id);

    if (!item) return;

    navigate("/add-categories", {
      state: {
        editType: "category",
        item,
      },
    });
  };

  const editSubCategory = (item: SubCategory) => {
    navigate("/add-categories", {
      state: {
        editType: "subcategory",
        item,
      },
    });
  };

  const deleteSubCategoryItem = async (item: SubCategory) => {
    const confirmation = await Swal.fire({
      title: `Delete ${item.name}?`,
      text: "Products using this subcategory will no longer be assigned to it.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const result = await deleteSubCategory(item._id);
      if (!result.success) {
        throw new Error(result.message || "Delete failed");
      }
      await Swal.fire({
        title: "Deleted",
        text: result.message || "Subcategory deleted successfully",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
      await loadData();
    } catch (error: any) {
      Swal.fire("Delete Failed", error.message, "error");
    }
  };

  const metrics = [
    {
      label: "Total Categories",
      value: categories.length,
      icon: "🗂️",
      tone: "purple",
    },
    {
      label: "Active Categories",
      value: categories.filter((item) => item.isActive === true).length,
      icon: "✅",
      tone: "green",
    },
    {
      label: "Total Subcategories",
      value: categories.reduce(
        (total, item) => total + (item.subCategories?.length || 0),
        0,
      ),
      icon: "🔗",
      tone: "orange",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Catalog</div>

          <h1 className="page-title">
            All <em>Categories</em>
          </h1>

          <div className="page-sub">Manage product categories and subcategories.</div>
        </div>

        <div className="page-hdr-actions">
          <NavLink to="/add-categories" className="btn-sm btn-primary">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add Category
          </NavLink>
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((item) => (
          <MetricCard key={item.label} metric={item} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>

          <input
            value={categorySearch}
            placeholder="Search categories..."
            onChange={(event) => setCategorySearch(event.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={isActive}
          onChange={(event) => setIsActive(event.target.value)}
        >
          <option value="all">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      <ListCard
        rows={paged.rows}
        loading={loading}
        productCount={productCount}
        onEdit={editItem}
        onDelete={deleteItem}
        onSubCategoryEdit={editSubCategory}
        onSubCategoryDelete={deleteSubCategoryItem}
        page={paged.page}
        limit={limit}
        total={paged.total}
        totalPages={paged.totalPages}
        onPageChange={setPage}
        onLimitChange={(nextLimit) => {
          setPage(1);
          setLimit(nextLimit);
        }}
      />
    </>
  );
};

type ListCardProps = {
  rows: Category[];
  loading: boolean;
  productCount: (item: Category) => number;
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onSubCategoryEdit: (item: SubCategory) => void;
  onSubCategoryDelete: (item: SubCategory) => void;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
};

const ListCard = ({
  rows,
  loading,
  productCount,
  onEdit,
  onDelete,
  onSubCategoryEdit,
  onSubCategoryDelete,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  onLimitChange,
}: ListCardProps) => {
  return (
    <section className="table-card">
      <div className="table-head">
        <h2>Category List</h2>
        <p>Manage product categories</p>
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Subcategories</th>
              <th>Slug</th>
              <th>Products</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  Loading categories...
                </td>
              </tr>
            ) : total === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No data found.
                </td>
              </tr>
            ) : (
              rows.map((item) => {
                return (
                  <tr key={item._id}>
                    <td>
                      <div className="img-name-cell">
                        <AppImage
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          crossOrigin="anonymous"
                        />

                        <div>
                          <strong>{item.name}</strong>
                          <small className="table-muted">{item.description || "No description"}</small>
                        </div>
                      </div>
                    </td>

                    <td>
                      {item.subCategories?.length ? (
                        <div className="category-subcategory-list">
                          {item.subCategories.map((subCategory) => (
                            <div
                              className="category-subcategory-entry"
                              key={subCategory._id}
                            >
                              <div
                                className="category-subcategory-chip"
                                title={subCategory.slug || subCategory.name}
                              >
                                {subCategory.image ? (
                                  <AppImage
                                    src={getImageUrl(subCategory.image)}
                                    alt=""
                                    crossOrigin="anonymous"
                                  />
                                ) : null}
                                <span>{subCategory.name}</span>
                              </div>
                              <div className="category-subcategory-actions">
                                <button
                                  type="button"
                                  aria-label={`Edit ${subCategory.name}`}
                                  title="Edit subcategory"
                                  onClick={() => onSubCategoryEdit(subCategory)}
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  aria-label={`Delete ${subCategory.name}`}
                                  title="Delete subcategory"
                                  onClick={() => onSubCategoryDelete(subCategory)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="table-muted">No subcategories</span>
                      )}
                    </td>

                    <td>{item.slug || "-"}</td>
                    <td>{productCount(item)}</td>

                    <td>
                      <span
                        className={`status-badge ${
                          item.isActive === true ? "success" : "danger"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td>
                      <div className="actions-btns order-actions">
                        <button
                          type="button"
                          className="action-button edit"
                          onClick={() => onEdit(item._id)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="action-button delete"
                          onClick={() => onDelete(item._id, item.name)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page}
        limit={limit}
        total={total}
        totalPages={totalPages}
        itemLabel="categories"
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </section>
  );
};

export default CategoriesList;
